// LIMS calculation engine (PRD-005 Area 2, SPEC-016). A SELF-WRITTEN, deterministic,
// side-effect-free expression evaluator + statistics + rounding + a per-parameter
// `evaluateParameter` + a template library (CALC_TEMPLATES). NO `eval`, NO `new Function`,
// NO property access — only numbers, the operators `+ - * / ^ ( )`, unary minus, named
// variables, and a WHITELISTED set of functions. This makes the engine GMP-validatable:
// every result is reproducible from the same inputs + formula, and the parser cannot run
// arbitrary code.
//
// Pure module — no React/Redux imports, no I/O. Importing it has zero effect unless its
// functions are called (so `showLims` off = no engine activity at all).
//
// ---------------------------------------------------------------------------------------
// Worked validation examples (hand-checked; see the self-tests in the SPEC-016 test step):
//
//   Assay (%LC), guide kap. 6.1:
//     formula: (area_sample / area_std) * (weight_std / weight_sample) * (purity / 100) * 100
//     inputs:  area_sample=2456800, area_std=2498700, weight_std=50.20, weight_sample=50.00,
//              purity=99.74
//     => (2456800/2498700) * (50.20/50.00) * (99.74/100) * 100
//      = 0.9832312... * 1.004 * 0.9974 * 100
//      ≈ 98.4598 %LC   (rounds to 98.46 at significantDigits=4 / 98.5 at 3)
//
//   mean([10, 12, 14])           = 12
//   sampleSd([10, 12, 14])       = 2            (n-1: sqrt(((−2)^2+0+2^2)/2) = sqrt(4) = 2)
//   rsd([10, 12, 14])            = 16.6666...%  (100 * 2 / 12)
//   roundToSignificant(98.4567, 4) = 98.46      (4 significant figures)
//   evaluate("2 + 3 * 4")        = 14
//   evaluate("-2 ^ 2")           = -4           (unary minus binds looser than ^, matching
//                                                spreadsheet-free math convention here)
//   evaluate("sqrt(9) + abs(-3)")= 6
//   evaluate("foo(1)")           => { error }   (unknown function — no code runs)
//   evaluate("1 + )")            => { error }   (syntax error — no crash)
// ---------------------------------------------------------------------------------------

// ===== Tokenizer =====================================================================

const TOKEN = {
  NUMBER: "number",
  IDENT: "ident",
  OP: "op",
  LPAREN: "lparen",
  RPAREN: "rparen",
  COMMA: "comma",
  EOF: "eof",
}

// Allowed binary operators (also `-` and `+` as unary prefix).
const OPERATORS = new Set(["+", "-", "*", "/", "^"])

const WHITESPACE = new Set([" ", "\t", "\n", "\r"])
const SINGLE_CHAR_TOKENS = { "(": TOKEN.LPAREN, ")": TOKEN.RPAREN, ",": TOKEN.COMMA }

// Scan a number literal starting at `start`. Returns { token, next } or { error }.
function scanNumber(src, start) {
  let j = start + 1
  let seenDot = src[start] === "."
  while (j < src.length) {
    const c = src[j]
    if (isDigit(c)) {
      j += 1
    } else if (c === "." && !seenDot) {
      seenDot = true
      j += 1
    } else {
      break
    }
  }
  const text = src.slice(start, j)
  const num = Number(text)
  if (!Number.isFinite(num)) return { error: `Invalid number: "${text}"` }
  return { token: { type: TOKEN.NUMBER, value: num }, next: j }
}

// Scan an identifier (variable / function name) starting at `start`. Returns { token, next }.
function scanIdent(src, start) {
  let j = start + 1
  while (j < src.length && isIdentPart(src[j])) j += 1
  return { token: { type: TOKEN.IDENT, value: src.slice(start, j) }, next: j }
}

// Tokenize a formula string. Returns { tokens } or { error } — never throws.
function tokenize(input) {
  const tokens = []
  const src = String(input == null ? "" : input)
  let i = 0
  while (i < src.length) {
    const ch = src[i]
    if (WHITESPACE.has(ch)) {
      i += 1
    } else if (isDigit(ch) || (ch === "." && isDigit(src[i + 1]))) {
      // Number (integer or decimal). The engine's canonical decimal separator is "."; input
      // values are normalised before evaluation and "," is reserved as the argument separator.
      const r = scanNumber(src, i)
      if (r.error) return { error: r.error }
      tokens.push(r.token)
      i = r.next
    } else if (isIdentStart(ch)) {
      const r = scanIdent(src, i)
      tokens.push(r.token)
      i = r.next
    } else if (SINGLE_CHAR_TOKENS[ch]) {
      tokens.push({ type: SINGLE_CHAR_TOKENS[ch] })
      i += 1
    } else if (OPERATORS.has(ch)) {
      tokens.push({ type: TOKEN.OP, value: ch })
      i += 1
    } else {
      return { error: `Unexpected character: "${ch}"` }
    }
  }
  tokens.push({ type: TOKEN.EOF })
  return { tokens }
}

function isDigit(c) {
  return c >= "0" && c <= "9"
}
function isIdentStart(c) {
  return (c >= "a" && c <= "z") || (c >= "A" && c <= "Z") || c === "_"
}
function isIdentPart(c) {
  return isIdentStart(c) || isDigit(c)
}

// ===== Parser (Pratt / precedence-climbing) ==========================================
//
// Grammar (precedence, low → high):
//   expr        := add
//   add         := mul ( ("+" | "-") mul )*
//   mul         := unary ( ("*" | "/") unary )*
//   unary       := ("-" | "+") unary | power
//   power       := primary ( "^" unary )?        // right-associative
//   primary     := NUMBER | IDENT | IDENT "(" args ")" | "(" expr ")"
//   args        := expr ( "," expr )*
//
// Produces a small AST of plain objects: { type, ... }. No code generation, no eval.

function parse(tokens) {
  let pos = 0
  const peek = () => tokens[pos]
  const next = () => tokens[pos++]

  function parseExpr() {
    return parseAdd()
  }

  function parseAdd() {
    let left = parseMul()
    if (left.error) return left
    while (peek().type === TOKEN.OP && (peek().value === "+" || peek().value === "-")) {
      const op = next().value
      const right = parseMul()
      if (right.error) return right
      left = { type: "binary", op, left, right }
    }
    return left
  }

  function parseMul() {
    let left = parseUnary()
    if (left.error) return left
    while (peek().type === TOKEN.OP && (peek().value === "*" || peek().value === "/")) {
      const op = next().value
      const right = parseUnary()
      if (right.error) return right
      left = { type: "binary", op, left, right }
    }
    return left
  }

  function parseUnary() {
    if (peek().type === TOKEN.OP && (peek().value === "-" || peek().value === "+")) {
      const op = next().value
      const operand = parseUnary()
      if (operand.error) return operand
      return { type: "unary", op, operand }
    }
    return parsePower()
  }

  function parsePower() {
    const base = parsePrimary()
    if (base.error) return base
    if (peek().type === TOKEN.OP && peek().value === "^") {
      next()
      // Right-associative: 2^3^2 = 2^(3^2). The exponent is a unary so "2 ^ -1" parses.
      const exponent = parseUnary()
      if (exponent.error) return exponent
      return { type: "binary", op: "^", left: base, right: exponent }
    }
    return base
  }

  // Parse the comma-separated argument list of a function call (the "(" already consumed).
  // Returns { args } or { error }; consumes the closing ")".
  function parseArgs() {
    const args = []
    if (peek().type !== TOKEN.RPAREN) {
      for (;;) {
        const arg = parseExpr()
        if (arg.error) return arg
        args.push(arg)
        if (peek().type === TOKEN.COMMA) {
          next()
          continue
        }
        break
      }
    }
    if (peek().type !== TOKEN.RPAREN) {
      return { error: "Expected ')' after function arguments" }
    }
    next() // consume ")"
    return { args }
  }

  // Parse an identifier — either a function call `name(...)` or a bare variable `name`.
  function parseIdent(name) {
    if (peek().type !== TOKEN.LPAREN) return { type: "variable", name }
    next() // consume "("
    const parsed = parseArgs()
    if (parsed.error) return parsed
    return { type: "call", name, args: parsed.args }
  }

  // Parse a parenthesised sub-expression (the "(" already consumed).
  function parseGroup() {
    const inner = parseExpr()
    if (inner.error) return inner
    if (peek().type !== TOKEN.RPAREN) return { error: "Expected ')'" }
    next()
    return inner
  }

  function parsePrimary() {
    const tok = next()
    switch (tok.type) {
      case TOKEN.NUMBER:
        return { type: "number", value: tok.value }
      case TOKEN.IDENT:
        return parseIdent(tok.value)
      case TOKEN.LPAREN:
        return parseGroup()
      case TOKEN.OP:
        return { error: `Unexpected operator "${tok.value}"` }
      default:
        return { error: "Unexpected end of expression" }
    }
  }

  const ast = parseExpr()
  if (ast.error) return ast
  if (peek().type !== TOKEN.EOF) {
    return { error: "Unexpected trailing input in formula" }
  }
  return { ast }
}

// ===== Statistics (used by aggregate functions/templates) ============================

function toFiniteNumbers(list) {
  return (Array.isArray(list) ? list : [list])
    .map((x) => (typeof x === "number" ? x : Number(x)))
    .filter((x) => Number.isFinite(x))
}

// Arithmetic mean of a numeric list (NaN for an empty list).
export function mean(list) {
  const nums = toFiniteNumbers(list)
  if (nums.length === 0) return NaN
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

// SAMPLE standard deviation (n-1 / Bessel's correction). NaN for fewer than 2 values.
export function sampleSd(list) {
  const nums = toFiniteNumbers(list)
  if (nums.length < 2) return NaN
  const m = mean(nums)
  const variance = nums.reduce((acc, x) => acc + (x - m) * (x - m), 0) / (nums.length - 1)
  return Math.sqrt(variance)
}

// Relative standard deviation (%): 100 * sampleSd / mean. NaN when the mean is 0 or undefined.
export function rsd(list) {
  const m = mean(list)
  const sd = sampleSd(list)
  if (!Number.isFinite(m) || m === 0 || !Number.isFinite(sd)) return NaN
  return (100 * sd) / m
}

// ===== Whitelisted functions =========================================================
//
// Each entry validates arity and only ever touches its numeric arguments. Aggregate
// functions (mean/sd/rsd/sum/count/min/max) accept either a single list argument
// (`mean(values)`) or several scalar arguments (`mean(1, 2, 3)`).

function collectArgs(args) {
  // If a single array was passed (an aggregate variable bound to a list), flatten it;
  // otherwise treat the args as scalars.
  if (args.length === 1 && Array.isArray(args[0])) return toFiniteNumbers(args[0])
  return toFiniteNumbers(args)
}

const FUNCTIONS = {
  sqrt: { arity: 1, fn: (a) => Math.sqrt(a) },
  abs: { arity: 1, fn: (a) => Math.abs(a) },
  round: { arity: [1, 2], fn: (a, d) => roundTo(a, d == null ? 0 : Math.trunc(d)) },
  mean: { variadic: true, fn: (...a) => mean(collectArgs(a)) },
  sd: { variadic: true, fn: (...a) => sampleSd(collectArgs(a)) },
  rsd: { variadic: true, fn: (...a) => rsd(collectArgs(a)) },
  sum: { variadic: true, fn: (...a) => collectArgs(a).reduce((x, y) => x + y, 0) },
  count: { variadic: true, fn: (...a) => collectArgs(a).length },
  min: { variadic: true, fn: (...a) => Math.min(...collectArgs(a)) },
  max: { variadic: true, fn: (...a) => Math.max(...collectArgs(a)) },
}

// The exported whitelist (names only) — used by the SpecEditor validator + the contract docs.
export const ALLOWED_FUNCTIONS = Object.keys(FUNCTIONS)

// The declared input sources of a calculated parameter (single source of truth — limsSpecs
// re-exports this for its normaliser, the SpecEditor builds its dropdown from the same set):
//   "manual"               — the analyst types the value at result entry
//   "standardPurity"       — auto-filled from the run's reference standard (purityPercent)
//   "standardWaterContent" — auto-filled from the run's reference standard (waterContentPercent)
//   "parameter"            — SPEC-023 (PRD-006 K22): bound to ANOTHER parameter of the same
//                            spec ({ ..., source: "parameter", parameterId }) — the input
//                            resolves to that parameter's ROUNDED value via evaluateSpec.
export const CALC_INPUT_SOURCES = ["manual", "standardPurity", "standardWaterContent", "parameter"]

// Decimal-place rounding (NOT significant figures). Used by the `round(x, d)` function.
function roundTo(value, digits) {
  if (!Number.isFinite(value)) return value
  const factor = Math.pow(10, digits)
  return Math.round((value + Number.EPSILON) * factor) / factor
}

// ===== AST evaluation ================================================================

// Resolve a `variable` node to a scalar number. Returns { value } or { error }. A list
// variable used in plain arithmetic is an error — lists are only valid inside aggregate fns.
function evalVariable(node, variables) {
  if (!Object.prototype.hasOwnProperty.call(variables, node.name)) {
    return { error: `Unknown variable: "${node.name}"` }
  }
  const v = variables[node.name]
  if (Array.isArray(v)) {
    return { error: `Variable "${node.name}" is a list — use it inside an aggregate function` }
  }
  const num = typeof v === "number" ? v : Number(v)
  if (!Number.isFinite(num)) return { error: `Variable "${node.name}" is not a number` }
  return { value: num }
}

// Apply a binary operator to two already-evaluated numeric operands.
function applyBinary(op, a, b) {
  switch (op) {
    case "+":
      return { value: a + b }
    case "-":
      return { value: a - b }
    case "*":
      return { value: a * b }
    case "/":
      return b === 0 ? { error: "Division by zero" } : { value: a / b }
    case "^":
      return { value: Math.pow(a, b) }
    default:
      return { error: `Unknown operator "${op}"` }
  }
}

function evalBinary(node, variables) {
  const left = evalNode(node.left, variables)
  if (left.error) return left
  const right = evalNode(node.right, variables)
  if (right.error) return right
  return applyBinary(node.op, left.value, right.value)
}

// Check a non-variadic function's argument count against its declared arity (number | [min,max]).
function arityOk(arity, count) {
  return Array.isArray(arity) ? count >= arity[0] && count <= arity[1] : count === arity
}

function evalCall(node, variables) {
  const def = FUNCTIONS[node.name]
  if (!def) return { error: `Unknown function: "${node.name}"` }
  const values = []
  for (const argNode of node.args) {
    // An aggregate function may receive a single LIST variable as one argument.
    if (def.variadic && argNode.type === "variable" && Array.isArray(variables[argNode.name])) {
      values.push(toFiniteNumbers(variables[argNode.name]))
      continue
    }
    const r = evalNode(argNode, variables)
    if (r.error) return r
    values.push(r.value)
  }
  if (!def.variadic && !arityOk(def.arity, values.length)) {
    return { error: `Function "${node.name}" called with the wrong number of arguments` }
  }
  const out = def.fn(...values)
  if (!Number.isFinite(out)) {
    return { error: `Function "${node.name}" produced a non-finite result` }
  }
  return { value: out }
}

// Evaluate a parsed AST against a `variables` map. Returns { value } or { error }.
// `variables` maps a name → number OR number[] (an aggregate input).
function evalNode(node, variables) {
  switch (node.type) {
    case "number":
      return { value: node.value }
    case "variable":
      return evalVariable(node, variables)
    case "unary": {
      const operand = evalNode(node.operand, variables)
      if (operand.error) return operand
      return { value: node.op === "-" ? -operand.value : operand.value }
    }
    case "binary":
      return evalBinary(node, variables)
    case "call":
      return evalCall(node, variables)
    default:
      return { error: "Invalid expression node" }
  }
}

// ===== Public evaluator ==============================================================

// Evaluate a formula string against a `variables` map (name → number | number[]).
// Returns { value } on success or { error } on any tokenizer/parser/eval failure — NEVER
// throws and NEVER executes arbitrary code. This is the GMP-safe entry point.
export function evaluate(formula, variables = {}) {
  if (typeof formula !== "string" || formula.trim() === "") {
    return { error: "Empty formula" }
  }
  const tk = tokenize(formula)
  if (tk.error) return { error: tk.error }
  const pr = parse(tk.tokens)
  if (pr.error) return { error: pr.error }
  const result = evalNode(pr.ast, variables)
  if (result.error) return { error: result.error }
  if (!Number.isFinite(result.value)) {
    return { error: "Formula did not produce a finite number" }
  }
  return { value: result.value }
}

// Validate that a formula only references the supplied variable names + whitelisted
// functions (and is syntactically parseable). Returns { ok: true } or { ok: false, error }.
// Used by the SpecEditor to block saving an invalid calculated-parameter formula.
export function validateFormula(formula, allowedVariableKeys = []) {
  if (typeof formula !== "string" || formula.trim() === "") {
    return { ok: false, error: "Empty formula" }
  }
  const tk = tokenize(formula)
  if (tk.error) return { ok: false, error: tk.error }
  const pr = parse(tk.tokens)
  if (pr.error) return { ok: false, error: pr.error }
  const allowed = new Set(allowedVariableKeys)
  const bad = []
  collectNames(pr.ast, allowed, bad)
  if (bad.length > 0) {
    return { ok: false, error: `Unknown name(s): ${Array.from(new Set(bad)).join(", ")}` }
  }
  return { ok: true }
}

// Walk the AST collecting any variable not in `allowedVars` and any function not whitelisted.
function collectNames(node, allowedVars, bad) {
  switch (node.type) {
    case "variable":
      if (!allowedVars.has(node.name)) bad.push(node.name)
      break
    case "unary":
      collectNames(node.operand, allowedVars, bad)
      break
    case "binary":
      collectNames(node.left, allowedVars, bad)
      collectNames(node.right, allowedVars, bad)
      break
    case "call":
      if (!FUNCTIONS[node.name]) bad.push(node.name + "()")
      node.args.forEach((a) => collectNames(a, allowedVars, bad))
      break
    default:
      break
  }
}

// ===== Rounding ======================================================================

// Round a value to `digits` SIGNIFICANT figures (not decimal places). Used ONLY on the
// final reported value — intermediates keep full precision. A null/invalid `digits`
// (or a non-finite value) returns the value unchanged. `0` significant digits is treated
// as "no rounding" (there is no meaningful 0-sig-fig value).
export function roundToSignificant(value, digits) {
  if (!Number.isFinite(value)) return value
  const d = Number(digits)
  if (!Number.isFinite(d) || d <= 0) return value
  if (value === 0) return 0
  const sign = value < 0 ? -1 : 1
  const abs = Math.abs(value)
  const power = Math.ceil(Math.log10(abs))
  const factor = Math.pow(10, d - power)
  return (sign * Math.round(abs * factor)) / factor
}

// ===== Per-parameter evaluation ======================================================
//
// Evaluate one CALCULATED spec parameter against the analyst's entered inputs.
//
//   parameter: {
//     formula: "<expr referencing the input keys>",
//     inputs: [{ key, label, unit, source }],
//     aggregate: { inputKey } | null,           // which input holds the n-value list
//     significantDigits: number | null,         // rounding of the final value
//     individualTolerancePercent: number | null // ±% individual limit for an aggregate
//   }
//   inputValues: { [inputKey]: number | string | (number|string)[] }   // raw analyst input
//                an aggregate input is an array of the n entered values
//   context: reserved (e.g. standard purity already resolved into inputValues by the caller)
//
// Returns:
//   {
//     computedValue: number | null,   // full precision (null on error / no input)
//     rounded: number | null,         // computedValue at significantDigits (the JUDGED value)
//     perValueChecks: [{ value, withinTolerance }] | null,  // aggregate individual-limit checks
//     error: string | null,
//   }
export function evaluateParameter(parameter, inputValues = {}, _context = {}) {
  const empty = { computedValue: null, rounded: null, perValueChecks: null, error: null }
  if (!parameter || typeof parameter.formula !== "string" || parameter.formula.trim() === "") {
    return { ...empty, error: "No formula" }
  }
  const aggregateKey = parameter.aggregate && parameter.aggregate.inputKey ? parameter.aggregate.inputKey : null

  // Build the variable map. An aggregate input becomes a number[]; scalars are normalised
  // (Danish comma → dot). Without every required input we cannot compute — but that is "not yet
  // entered", NOT an error.
  const built = buildVariables(parameter.inputs, inputValues, aggregateKey)
  if (built.anyMissing) return { ...empty }

  const out = evaluate(parameter.formula, built.variables)
  if (out.error) return { ...empty, error: out.error }

  const computedValue = out.value
  const rounded = roundToSignificant(computedValue, parameter.significantDigits)
  const perValueChecks = aggregateChecks(built.aggregateList, parameter.individualTolerancePercent)
  return { computedValue, rounded, perValueChecks, error: null }
}

// Resolve the declared inputs into the engine's variable map. Returns
// { variables, aggregateList, anyMissing } — `anyMissing` when any scalar is blank or the
// aggregate list is empty (so the caller treats it as "not yet entered").
function buildVariables(inputsRaw, inputValues, aggregateKey) {
  const inputs = Array.isArray(inputsRaw) ? inputsRaw : []
  const variables = {}
  let aggregateList = null
  let anyMissing = false
  for (const input of inputs) {
    const raw = inputValues[input.key]
    if (input.key === aggregateKey) {
      aggregateList = normaliseList(raw)
      variables[input.key] = aggregateList
      if (aggregateList.length === 0) anyMissing = true
    } else {
      const num = normaliseScalar(raw)
      if (num === null) anyMissing = true
      else variables[input.key] = num
    }
  }
  return { variables, aggregateList, anyMissing }
}

// Aggregate individual-limit check: each entered value within ±tolerance% of the mean. Returns
// null when there is no aggregate list or no usable tolerance.
function aggregateChecks(aggregateList, individualTolerancePercent) {
  if (!Array.isArray(aggregateList) || aggregateList.length === 0 || individualTolerancePercent == null) {
    return null
  }
  const tol = Number(individualTolerancePercent)
  if (!Number.isFinite(tol) || tol <= 0) return null
  const m = mean(aggregateList)
  const lower = m * (1 - tol / 100)
  const upper = m * (1 + tol / 100)
  const lo = Math.min(lower, upper)
  const hi = Math.max(lower, upper)
  return aggregateList.map((value) => ({ value, withinTolerance: value >= lo && value <= hi }))
}

// Normalise one scalar raw input (string with Danish comma, number, or blank) to a finite
// number, or null when blank / non-numeric.
function normaliseScalar(raw) {
  if (raw === "" || raw === null || raw === undefined) return null
  const num = Number(typeof raw === "string" ? raw.replace(",", ".") : raw)
  return Number.isFinite(num) ? num : null
}

// Normalise a raw aggregate input (array of values, or a single value) to a number[] of the
// finite entries only.
function normaliseList(raw) {
  let arr
  if (Array.isArray(raw)) arr = raw
  else if (raw === undefined || raw === null || raw === "") arr = []
  else arr = [raw]
  return arr.map(normaliseScalar).filter((x) => x !== null)
}

// ===== Spec-level evaluation (SPEC-023 — cross-parameter references, PRD-006 K22) ====
//
// A calculated parameter may declare an input with source "parameter" + `parameterId`: the
// input binds to the referenced sibling parameter's ROUNDED value — measured: the entered
// value; calculated: its computed value at significantDigits — because the rounded value is
// the GMP-judged/reported figure (chains must build on the documented number, and FE/BE must
// judge identically; the backend port mirrors this section for golden-vector parity).
//
// Shared contract semantics (SPEC-023 / backend SPEC-009):
//   - evaluation runs in dependency order (topological sort over the reference edges);
//   - a missing source value (blank / non-numeric / itself pending or erroring) makes the
//     dependent parameter PENDING — the same semantics as an empty manual input, NOT an error;
//   - a reference to an unknown parameter, a self-reference or a reference cycle is a
//     VALIDATION error before save ("Unknown parameter reference: …" / the chain-naming
//     "Circular reference: A → B → A") — and, defence in depth, the engine below detects the
//     same conditions at evaluation time and can never hang on a cyclic spec.

// A spec parameter is CALCULATED when its kind says so. String literal mirrored from
// limsSpecs.SPEC_PARAMETER_KIND.CALCULATED — NOT imported, so the engine stays a pure,
// dependency-free module (limsSpecs imports from here, never the other way around).
function isCalculatedKind(parameter) {
  return !!parameter && parameter.kind === "calculated"
}

// A parameter's display name for validation messages (its name, else its id — never blank).
function parameterDisplayName(parameter) {
  if (parameter && typeof parameter.name === "string" && parameter.name.trim() !== "") {
    return parameter.name
  }
  return (parameter && parameter.id) || "?"
}

// Whether `targetId` is reachable from any of `seedIds` following reference edges in `graph`
// ({ [parameterId]: [referenced parameterIds] }). Iterative DFS with an explicit stack and a
// visited set — bounded by the node count, never recurses, never hangs (cycle-safe by design).
function reaches(seedIds, targetId, graph) {
  const stack = [...seedIds]
  const seen = new Set()
  while (stack.length > 0) {
    const id = stack.pop()
    if (id === targetId) return true
    if (seen.has(id)) continue
    seen.add(id)
    const deps = graph[id] || []
    for (const dep of deps) stack.push(dep)
  }
  return false
}

// The human-readable reference cycle starting (and ending) at `startId`: "A → B → A". Each hop
// follows the FIRST dependency (declaration order) that leads back to the start — deterministic
// — and the walk is bounded by the node count, so it terminates even on malformed graphs.
// A self-reference is simply the 1-cycle "A → A" (no special-casing).
function cycleChainText(startId, graph, parametersById) {
  const names = [parameterDisplayName(parametersById[startId])]
  let current = startId
  const bound = Object.keys(graph).length + 1
  for (let i = 0; i < bound; i++) {
    const deps = graph[current] || []
    const next = deps.find((d) => d === startId || reaches(graph[d] || [], startId, graph))
    if (next === undefined) break // defensive — a true cycle member always has a way back
    names.push(parameterDisplayName(parametersById[next]))
    if (next === startId) break
    current = next
  }
  return names.join(" → ")
}

// Analyse a spec's cross-parameter reference structure. Returns
//   { order, errors }
//   - order:  the evaluable CALCULATED parameters in dependency order (stable: among ready
//             parameters, spec declaration order wins)
//   - errors: { [parameterId]: message } — one message per STRUCTURALLY invalid calculated
//             parameter: an input referencing a missing/unknown parameterId
//             (`Unknown parameter reference: "<input key>"`), or membership of a reference
//             cycle / self-reference (`Circular reference: A → B → A`, names along the cycle
//             starting at the flagged parameter).
// A parameter that merely DEPENDS on an erroneous/cyclic one is NOT flagged — at evaluation it
// is pending (its source never produces a value), and at validation the root cause is the one
// flagged. Used by evaluateSpec (runtime, defensive) and validateSpecReferences (editor/save).
function analyzeSpecReferences(parametersRaw) {
  const parameters = Array.isArray(parametersRaw) ? parametersRaw : []
  const parametersById = {}
  parameters.forEach((p) => {
    if (p && p.id) parametersById[p.id] = p
  })

  const errors = {}
  const calculated = parameters.filter((p) => p && p.id && isCalculatedKind(p))

  // Reference edges (calculated → referenced CALCULATED sibling). A reference to a MEASURED
  // parameter needs no ordering edge — it resolves directly from the entered value. A missing/
  // unknown parameterId is a structural error on the referencing parameter.
  const depsOf = {}
  calculated.forEach((p) => {
    const deps = []
    ;(p.inputs || []).forEach((input) => {
      if (!input || input.source !== "parameter") return
      const refId = input.parameterId
      if (!refId || !parametersById[refId]) {
        if (!errors[p.id]) {
          errors[p.id] = `Unknown parameter reference: "${input.key || input.label || "?"}"`
        }
        return
      }
      if (isCalculatedKind(parametersById[refId])) deps.push(refId)
    })
    depsOf[p.id] = deps
  })

  // Structurally broken parameters (unknown refs) leave the graph; their dependents resolve a
  // null source value at evaluation (→ pending), keeping the error attached to the root cause.
  const valid = calculated.filter((p) => !errors[p.id])
  const validIds = new Set(valid.map((p) => p.id))
  const graph = {}
  valid.forEach((p) => {
    graph[p.id] = depsOf[p.id].filter((d) => validIds.has(d))
  })

  // Topological order (Kahn-style), stable in declaration order: repeatedly take the FIRST
  // not-yet-ordered parameter whose dependencies are all ordered. O(n²) over a per-spec
  // parameter count of at most a few dozen — simple and deterministic beats clever here.
  const order = []
  const done = new Set()
  for (;;) {
    const next = valid.find((p) => !done.has(p.id) && graph[p.id].every((d) => done.has(d)))
    if (!next) break
    done.add(next.id)
    order.push(next)
  }

  // Anything left sits on — or behind — a reference cycle. Members of a cycle get the explicit
  // chain error; parameters merely depending on a cycle stay un-ordered (→ pending).
  valid.forEach((p) => {
    if (done.has(p.id)) return
    if (reaches(graph[p.id] || [], p.id, graph)) {
      errors[p.id] = `Circular reference: ${cycleChainText(p.id, graph, parametersById)}`
    }
  })

  return { order, errors }
}

// Validate a spec's cross-parameter references (SPEC-023) — the spec-level companion to the
// per-formula validateFormula. Returns { ok: true } or { ok: false, errors } where `errors`
// maps parameterId → message ("Unknown parameter reference: …" / "Circular reference: A → B →
// A"). The SpecEditor blocks saving on !ok — the same contract the backend enforces with a 422
// at definition save — so an analyst can never reach result entry with a cyclic spec.
export function validateSpecReferences(parameters) {
  const { errors } = analyzeSpecReferences(parameters)
  if (Object.keys(errors).length === 0) return { ok: true }
  return { ok: false, errors }
}

// Evaluate ALL of a spec's calculated parameters against one sample's entries, resolving
// cross-parameter references in dependency order (SPEC-023).
//
//   parameters: the spec's full parameter list (measured + calculated)
//   entries: {
//     values: { [parameterId]: raw },                          // measured entered values
//     inputs: { [parameterId]: { [inputKey]: raw | raw[] } },  // calculated raw inputs
//   }
//
// Returns { byId: { [parameterId]: result } } where a CALCULATED parameter's result is
// evaluateParameter's shape + `resolvedInputs`:
//   { computedValue, rounded, perValueChecks, error, resolvedInputs }
//   - resolvedInputs: the input map ACTUALLY evaluated — the raw entries with every
//     parameter-sourced key replaced by the resolved source value (or absent while the source
//     has none). This is what result records snapshot for GMP reproducibility.
// and a MEASURED parameter's result is
//   { computedValue: null, rounded: <entered value | null>, perValueChecks: null, error: null,
//     resolvedInputs: null }
// (a measured parameter's "rounded" is its entered value — what a reference to it binds to).
//
// Pending vs error: a parameter whose source has no value yet (or whose source errored, or
// which depends on a cycle it is not itself part of) evaluates to the PENDING result
// ({ computedValue: null, error: null }) — exactly like an empty manual input. Only structural
// problems (cycle membership, unknown reference) and formula-evaluation failures carry `error`.
export function evaluateSpec(parametersRaw, entries = {}) {
  const parameters = Array.isArray(parametersRaw) ? parametersRaw : []
  const values = (entries && entries.values) || {}
  const inputsByParameter = (entries && entries.inputs) || {}
  const { order, errors } = analyzeSpecReferences(parameters)

  const byId = {}
  // What a reference to each parameter binds to: its ROUNDED value, or null while unavailable.
  const sourceValue = {}

  // Measured parameters resolve directly from their entered raw value.
  parameters.forEach((p) => {
    if (!p || !p.id || isCalculatedKind(p)) return
    const entered = normaliseScalar(values[p.id])
    sourceValue[p.id] = entered
    byId[p.id] = { computedValue: null, rounded: entered, perValueChecks: null, error: null, resolvedInputs: null }
  })

  // Structurally invalid calculated parameters (unknown reference / cycle member) error
  // WITHOUT evaluating — and never provide a source value to their dependents.
  parameters.forEach((p) => {
    if (!p || !p.id || !isCalculatedKind(p) || !errors[p.id]) return
    sourceValue[p.id] = null
    byId[p.id] = { computedValue: null, rounded: null, perValueChecks: null, error: errors[p.id], resolvedInputs: null }
  })

  // Evaluate the orderable calculated parameters in dependency order.
  order.forEach((p) => {
    const raw = inputsByParameter[p.id] || {}
    const resolved = { ...raw }
    ;(p.inputs || []).forEach((input) => {
      if (!input || input.source !== "parameter") return
      const v = sourceValue[input.parameterId]
      if (v === null || v === undefined) {
        // No source value yet → the dependent parameter is pending. Drop any stale entry under
        // the key: a parameter-bound input only ever exists RESOLVED, never analyst-typed.
        delete resolved[input.key]
      } else {
        resolved[input.key] = v
      }
    })
    const out = evaluateParameter(p, resolved)
    byId[p.id] = { ...out, resolvedInputs: resolved }
    sourceValue[p.id] = out.error ? null : out.rounded
  })

  // Calculated parameters that could not be ordered (they depend on an erroneous or cyclic
  // sibling without being structurally broken themselves) are pending.
  parameters.forEach((p) => {
    if (!p || !p.id || !isCalculatedKind(p) || byId[p.id]) return
    sourceValue[p.id] = null
    byId[p.id] = { computedValue: null, rounded: null, perValueChecks: null, error: null, resolvedInputs: null }
  })

  return { byId }
}

// ===== Template library ==============================================================
//
// Quick-start templates for the SpecEditor. Each pre-fills a calculated parameter's
// `inputs`, `formula`, default criterion and (where relevant) `aggregate` +
// `individualTolerancePercent`. `labelKey`/input `labelKey` are i18n keys (resolved by the
// editor). `source` marks an input the result panel can auto-fill from a reference standard.
//
// These are PLAIN DATA — applying a template simply copies these fields onto the parameter.
export const CALC_TEMPLATES = [
  {
    id: "assay",
    labelKey: "limsCalcTemplateAssay",
    unit: "%",
    significantDigits: 4,
    criterionType: "range",
    min: 98.0,
    max: 102.0,
    aggregate: null,
    individualTolerancePercent: null,
    // %LC = (sample area / standard area) * (standard weight / sample weight) * (purity/100) * 100
    formula: "(area_sample / area_std) * (weight_std / weight_sample) * (purity / 100) * 100",
    inputs: [
      { key: "area_sample", labelKey: "limsCalcInputAreaSample", unit: "", source: "manual" },
      { key: "area_std", labelKey: "limsCalcInputAreaStd", unit: "", source: "manual" },
      { key: "weight_std", labelKey: "limsCalcInputWeightStd", unit: "mg", source: "manual" },
      { key: "weight_sample", labelKey: "limsCalcInputWeightSample", unit: "mg", source: "manual" },
      { key: "purity", labelKey: "limsCalcInputPurity", unit: "%", source: "standardPurity" },
    ],
  },
  {
    id: "relatedSubstances",
    labelKey: "limsCalcTemplateRelatedSubstances",
    unit: "%",
    significantDigits: 3,
    criterionType: "max",
    min: null,
    max: 0.5,
    aggregate: null,
    individualTolerancePercent: null,
    // Impurity % (area normalisation): impurity area / total area * 100
    formula: "(area_impurity / area_total) * 100",
    inputs: [
      { key: "area_impurity", labelKey: "limsCalcInputAreaImpurity", unit: "", source: "manual" },
      { key: "area_total", labelKey: "limsCalcInputAreaTotal", unit: "", source: "manual" },
    ],
  },
  {
    id: "karlFischer",
    labelKey: "limsCalcTemplateKarlFischer",
    unit: "%",
    significantDigits: 3,
    criterionType: "max",
    min: null,
    max: 0.5,
    aggregate: null,
    individualTolerancePercent: null,
    // Water % = (titrant volume * titre factor mg/mL water) / (sample weight mg) * 100
    formula: "(titrant_volume * titre_factor) / sample_weight_mg * 100",
    inputs: [
      { key: "titrant_volume", labelKey: "limsCalcInputTitrantVolume", unit: "mL", source: "manual" },
      { key: "titre_factor", labelKey: "limsCalcInputTitreFactor", unit: "mg/mL", source: "standardWaterContent" },
      { key: "sample_weight_mg", labelKey: "limsCalcInputSampleWeightMg", unit: "mg", source: "manual" },
    ],
  },
  {
    id: "ph",
    labelKey: "limsCalcTemplatePh",
    unit: "",
    significantDigits: 2,
    criterionType: "range",
    min: 6.8,
    max: 7.4,
    aggregate: null,
    individualTolerancePercent: null,
    // pH is a direct reading; the "formula" is the identity of the entered measurement so the
    // engine path is uniform (measured value flows through evaluate unchanged).
    formula: "ph_reading",
    inputs: [{ key: "ph_reading", labelKey: "limsCalcInputPhReading", unit: "", source: "manual" }],
  },
  {
    id: "weightUniformity",
    labelKey: "limsCalcTemplateWeightUniformity",
    unit: "%",
    significantDigits: 3,
    criterionType: "max",
    min: null,
    max: 6.0, // RSD ≤ 6 %
    aggregate: { inputKey: "weights" },
    individualTolerancePercent: 15, // each unit within ±15 % of the mean
    // Aggregate: the judged value is the RSD (%) of the n individual weights.
    formula: "rsd(weights)",
    inputs: [{ key: "weights", labelKey: "limsCalcInputWeights", unit: "mg", source: "manual" }],
  },
]

// Look up a template by id (or undefined).
export function getTemplate(id) {
  return CALC_TEMPLATES.find((tpl) => tpl.id === id)
}
